import { getFullUrl } from "../../../services/api/config";
import {
  PatientAddress,
  AddPatientAddressPayload,
  NearestClinicData,
  UpdateOrderAddressPayload,
} from "./types";

export class AddressService {
  private static getToken(): string | null {
    return localStorage.getItem("token");
  }

  private static headers(): Record<string, string> {
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${this.getToken()}`,
    };
  }

  /**
   * Fetch all saved addresses for a patient
   */
  static async fetchPatientAddresses(
    patientId: string
  ): Promise<{ success: boolean; message?: string; data?: PatientAddress[] }> {
    try {
      const url = `${getFullUrl("/patientservice/patient/address/fetch")}?patientId=${encodeURIComponent(patientId)}`;
      const res = await fetch(url, {
        method: "GET",
        headers: this.headers(),
      });
      const data = await res.json();
      return data;
    } catch (error) {
      console.error("Error fetching patient addresses:", error);
      return { success: false, message: "Failed to fetch addresses" };
    }
  }

  /**
   * Add new address for a patient
   */
  static async addPatientAddress(
    payload: AddPatientAddressPayload
  ): Promise<{ success: boolean; message?: string; data?: PatientAddress }> {
    try {
      const url = getFullUrl("/patientservice/patient/address/add");
      const res = await fetch(url, {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      return data;
    } catch (error) {
      console.error("Error adding patient address:", error);
      return { success: false, message: "Failed to save address" };
    }
  }

  /**
   * Get nearest clinic for an address
   */
  static async getNearestClinic(params: {
    patientAddress: string;
    pincode: string;
    latitude: number;
    longitude: number;
  }): Promise<{ success: boolean; message?: string; data?: NearestClinicData }> {
    try {
      const searchParams = new URLSearchParams({
        patientAddress: params.patientAddress,
        pincode: params.pincode,
        latitude: params.latitude.toString(),
        longitude: params.longitude.toString(),
      });
      const url = `${getFullUrl("/orderservice/online-order/get-nearest-clinic")}?${searchParams}`;
      const res = await fetch(url, {
        method: "GET",
        headers: this.headers(),
      });
      const data = await res.json();
      return data;
    } catch (error) {
      console.error("Error fetching nearest clinic:", error);
      return { success: false, message: "Failed to fetch nearest clinic" };
    }
  }

  /**
   * Update address & assigned clinic on an order
   */
  static async updateOrderAddress(
    payload: UpdateOrderAddressPayload
  ): Promise<{ success: boolean; message?: string; data?: any }> {
    try {
      const url = getFullUrl("/orderservice/admin-order/update-address");
      const res = await fetch(url, {
        method: "PUT",
        headers: this.headers(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok && !data.success) {
        // Fallback to /orderservice/online-order/update-address if endpoint variant
        const fallbackUrl = getFullUrl("/orderservice/online-order/update-address");
        const fallbackRes = await fetch(fallbackUrl, {
          method: "PUT",
          headers: this.headers(),
          body: JSON.stringify(payload),
        });
        return await fallbackRes.json();
      }
      return data;
    } catch (error) {
      console.error("Error updating order address:", error);
      return { success: false, message: "Failed to update order address" };
    }
  }
}
